'use client'
import { useRef, useMemo, useEffect, memo } from 'react'
import { Canvas, useFrame, useThree } from '@react-three/fiber'
import * as THREE from 'three'

export interface AudioState {
  playing: boolean
  fading: boolean
  audio: HTMLAudioElement | null
}

function Particles({ count = 3000 }) {
  const ref = useRef<THREE.Points>(null)
  const { viewport } = useThree()
  const fadeOpacity = useRef(0)

  const dims = useMemo(() => ({
    w: viewport.width * 1.5,
    h: viewport.height * 1.5,
  }), [])

  const [positions, colors, sizes] = useMemo(() => {
    const { w, h } = dims
    const pos = new Float32Array(count * 3)
    const col = new Float32Array(count * 3)
    const siz = new Float32Array(count)

    for (let i = 0; i < count; i++) {
      pos[i * 3] = (Math.random() - 0.5) * w
      pos[i * 3 + 1] = (Math.pow(Math.random(), 1.3) - 0.5) * h
      pos[i * 3 + 2] = (Math.random() - 0.5) * 15 - 5
      const t = Math.random()
      col[i * 3] = 0.31 + t * 0.18
      col[i * 3 + 1] = 0.28 + t * 0.15
      col[i * 3 + 2] = 0.9 - t * 0.15
      siz[i] = Math.random() * 1.5 + 0.5
    }
    return [pos, col, siz]
  }, [count])

  useFrame((state, delta) => {
    if (ref.current) {
      if (fadeOpacity.current < 0.6) {
        fadeOpacity.current = Math.min(0.6, fadeOpacity.current + delta * 0.6)
        ;(ref.current.material as THREE.PointsMaterial).opacity = fadeOpacity.current
      }

      const pos = ref.current.geometry.attributes.position.array as Float32Array
      for (let i = 0; i < count; i++) {
        pos[i * 3 + 1] += Math.sin(state.clock.elapsedTime * 0.1 + i * 0.01) * 0.0003
        pos[i * 3] += Math.cos(state.clock.elapsedTime * 0.08 + i * 0.01) * 0.0003
      }
      ref.current.geometry.attributes.position.needsUpdate = true
    }
  })

  return (
    <points ref={ref}>
      <bufferGeometry>
        <bufferAttribute attach="attributes-position" count={count} array={positions} itemSize={3} />
        <bufferAttribute attach="attributes-color" count={count} array={colors} itemSize={3} />
        <bufferAttribute attach="attributes-size" count={count} array={sizes} itemSize={1} />
      </bufferGeometry>
      <pointsMaterial
        size={0.03}
        vertexColors
        transparent
        opacity={0}
        sizeAttenuation
        depthWrite={false}
        blending={THREE.AdditiveBlending}
      />
    </points>
  )
}

const SEGMENTS = 50
const MAX_STARS = 4
const TOTAL = SEGMENTS * MAX_STARS

const starVert = `
  attribute float aSize;
  attribute float aAlpha;
  attribute vec3 aColor;
  varying float vAlpha;
  varying vec3 vColor;
  void main() {
    vAlpha = aAlpha;
    vColor = aColor;
    vec4 mv = modelViewMatrix * vec4(position, 1.0);
    gl_PointSize = aSize * (300.0 / -mv.z);
    gl_Position = projectionMatrix * mv;
  }
`

const starFrag = `
  uniform sampler2D uTexture;
  varying float vAlpha;
  varying vec3 vColor;
  void main() {
    vec4 tex = texture2D(uTexture, gl_PointCoord);
    gl_FragColor = vec4(vColor, tex.a * vAlpha);
  }
`

interface ShootingStarsProps {
  heroRef: React.RefObject<HTMLElement | null>
  audioStateRef: React.MutableRefObject<AudioState>
}

function ShootingStars({ heroRef, audioStateRef }: ShootingStarsProps) {
  const ref = useRef<THREE.Points>(null)
  const nextSpawn = useRef(2)

  const clickCount = useRef(0)
  const lastClickHandled = useRef(0)
  const eggPlayed = useRef(false)
  const eggPaused = useRef(false)

  const audioRef = useRef<HTMLAudioElement | null>(null)
  const inactivityTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const fadeInterval = useRef<ReturnType<typeof setInterval> | null>(null)
  const eggActive = useRef(false)

  const cancelFade = useRef(() => {
    if (fadeInterval.current) {
      clearInterval(fadeInterval.current)
      fadeInterval.current = null
    }
  })

  const startFadeOut = useRef(() => {
    if (!audioRef.current) return
    eggActive.current = false
    const steps = 40
    let step = 0
    cancelFade.current()
    audioStateRef.current = { ...audioStateRef.current, fading: true }
    fadeInterval.current = setInterval(() => {
      step++
      const vol = Math.max(0, 1 - step / steps)
      if (audioRef.current) audioRef.current.volume = vol
      if (step >= steps) {
        cancelFade.current()
        if (audioRef.current) {
          audioRef.current.pause()
          eggPaused.current = true
        }
        audioStateRef.current = { playing: false, fading: false, audio: audioRef.current }
      }
    }, 100)
  })

  const resetInactivity = useRef(() => {
    cancelFade.current()
    if (audioRef.current) audioRef.current.volume = 1
    eggActive.current = true
    audioStateRef.current = { ...audioStateRef.current, fading: false }
    if (inactivityTimer.current) clearTimeout(inactivityTimer.current)
    inactivityTimer.current = setTimeout(() => startFadeOut.current(), 15000)
  })

  useEffect(() => {
    const hero = heroRef.current
    if (!hero) return

    const handler = (e: MouseEvent) => {
      clickCount.current++

      if (clickCount.current >= 30 && !eggPlayed.current) {
        eggPlayed.current = true
        try {
          const audio = new Audio('/Majid Entezami - Khorramshahr Symphony 2.mp3')
          audio.loop = true
          audio.volume = 1
          audio.play().catch(() => {})
          audioRef.current = audio
          eggActive.current = true
          eggPaused.current = false
          audioStateRef.current = { playing: true, fading: false, audio }
          resetInactivity.current()
        } catch {}
        return
      }

      if (eggPlayed.current) {
        if (eggPaused.current && audioRef.current) {
          audioRef.current.play().catch(() => {})
          audioRef.current.volume = 1
          eggPaused.current = false
          eggActive.current = true
          audioStateRef.current = { playing: true, fading: false, audio: audioRef.current }
        }
        resetInactivity.current()
      }
    }

    hero.addEventListener('click', handler)
    return () => {
      hero.removeEventListener('click', handler)
      cancelFade.current()
      if (inactivityTimer.current) clearTimeout(inactivityTimer.current)
      if (audioRef.current) {
        audioRef.current.pause()
        audioRef.current = null
      }
    }
  }, [heroRef, audioStateRef])

  const stars = useRef<Array<{
    active: boolean
    sx: number; sy: number; sz: number
    ex: number; ey: number; ez: number
    progress: number
    speed: number
    trailLen: number
    isYellow: boolean
  }>>([])

  if (stars.current.length === 0) {
    for (let i = 0; i < MAX_STARS; i++) {
      stars.current.push({
        active: false, sx: 0, sy: 0, sz: 0,
        ex: 0, ey: 0, ez: 0,
        progress: 0, speed: 0, trailLen: 0.12, isYellow: false,
      })
    }
  }

  const posA = useRef(new Float32Array(TOTAL * 3))
  const sizeA = useRef(new Float32Array(TOTAL))
  const alphaA = useRef(new Float32Array(TOTAL))
  const colA = useRef(new Float32Array(TOTAL * 3))

  for (let i = 0; i < TOTAL; i++) {
    posA.current[i * 3 + 1] = -20
    sizeA.current[i] = 0
    alphaA.current[i] = 0
  }

  const texture = useMemo(() => {
    const c = document.createElement('canvas')
    c.width = 64; c.height = 64
    const ctx = c.getContext('2d')!
    const g = ctx.createRadialGradient(32, 32, 0, 32, 32, 32)
    g.addColorStop(0, 'rgba(255,255,255,1)')
    g.addColorStop(0.3, 'rgba(255,255,255,0.6)')
    g.addColorStop(0.6, 'rgba(200,220,255,0.15)')
    g.addColorStop(1, 'rgba(255,255,255,0)')
    ctx.fillStyle = g
    ctx.fillRect(0, 0, 64, 64)
    const t = new THREE.CanvasTexture(c)
    t.needsUpdate = true
    return t
  }, [])

  const tempColor = useMemo(() => new THREE.Color(), [])
  const whiteCol = useMemo(() => new THREE.Color('#ffffff'), [])
  const yellowCol = useMemo(() => new THREE.Color('#ffe9a8'), [])

  const spawnStar = (slot: typeof stars.current[0], yellow: boolean) => {
    const sx = -4 - Math.random() * 3
    const sy = 3 + Math.random() * 3
    const ex = 2 + Math.random() * 4
    const ey = -3 - Math.random() * 2
    const sz = -4 - Math.random() * 3
    slot.active = true
    slot.sx = sx; slot.sy = sy; slot.sz = sz
    slot.ex = ex; slot.ey = ey; slot.ez = sz + (Math.random() - 0.5) * 2
    slot.progress = 0
    slot.speed = 0.5 + Math.random() * 0.3
    slot.trailLen = yellow ? 0.12 : 0.16
    slot.isYellow = yellow
  }

  useFrame((state, delta) => {
    if (clickCount.current !== lastClickHandled.current) {
      lastClickHandled.current = clickCount.current
      const slot = stars.current.find(s => !s.active)
      if (slot) spawnStar(slot, true)
    }

    nextSpawn.current -= delta
    if (nextSpawn.current <= 0) {
      const slot = stars.current.find(s => !s.active)
      if (slot) spawnStar(slot, false)
      nextSpawn.current = 12 + Math.random() * 6
    }

    let idx = 0
    for (const star of stars.current) {
      if (!star.active) {
        for (let i = 0; i < SEGMENTS; i++) {
          posA.current[idx * 3 + 1] = -20
          sizeA.current[idx] = 0
          alphaA.current[idx] = 0
          idx++
        }
        continue
      }

      star.progress += delta * star.speed
      if (star.progress >= 1) {
        star.active = false
        for (let i = 0; i < SEGMENTS; i++) {
          posA.current[idx * 3 + 1] = -20
          sizeA.current[idx] = 0
          alphaA.current[idx] = 0
          idx++
        }
        continue
      }

      for (let i = 0; i < SEGMENTS; i++) {
        const t = i / SEGMENTS
        const p = Math.max(0, star.progress - t * star.trailLen)
        const e = p

        posA.current[idx * 3] = star.sx + (star.ex - star.sx) * e
        posA.current[idx * 3 + 1] = star.sy + (star.ey - star.sy) * e
        posA.current[idx * 3 + 2] = star.sz + (star.ez - star.sz) * e

        if (p <= 0) {
          sizeA.current[idx] = 0
          alphaA.current[idx] = 0
        } else {
          const fade = Math.max(0, 1 - t * 1.1)
          sizeA.current[idx] = fade * 0.3 + 0.015
          alphaA.current[idx] = fade * fade * 2

          tempColor.copy(star.isYellow ? yellowCol : whiteCol)
          colA.current[idx * 3] = tempColor.r
          colA.current[idx * 3 + 1] = tempColor.g
          colA.current[idx * 3 + 2] = tempColor.b
        }
        idx++
      }
    }

    if (ref.current) {
      const g = ref.current.geometry
      g.attributes.position.needsUpdate = true
      g.attributes.aSize.needsUpdate = true
      g.attributes.aAlpha.needsUpdate = true
      g.attributes.aColor.needsUpdate = true
    }
  })

  return (
    <points ref={ref} frustumCulled={false}>
      <bufferGeometry>
        <bufferAttribute attach="attributes-position" count={TOTAL} array={posA.current} itemSize={3} />
        <bufferAttribute attach="attributes-aSize" count={TOTAL} array={sizeA.current} itemSize={1} />
        <bufferAttribute attach="attributes-aAlpha" count={TOTAL} array={alphaA.current} itemSize={1} />
        <bufferAttribute attach="attributes-aColor" count={TOTAL} array={colA.current} itemSize={3} />
      </bufferGeometry>
      <shaderMaterial
        uniforms={{ uTexture: { value: texture } }}
        vertexShader={starVert}
        fragmentShader={starFrag}
        transparent
        depthWrite={false}
        blending={THREE.AdditiveBlending}
      />
    </points>
  )
}

function MouseTracker() {
  const { pointer } = useThree()
  const light = useRef<THREE.DirectionalLight>(null)

  useFrame(() => {
    if (light.current) {
      light.current.position.x = pointer.x * 8
      light.current.position.y = pointer.y * 8
    }
  })

  return <directionalLight ref={light} intensity={0.6} color="#5983e0" />
}

interface Scene3DContentProps {
  heroRef: React.RefObject<HTMLElement | null>
  audioStateRef: React.MutableRefObject<AudioState>
}

const Scene3DContent = memo(function Scene3DContent({ heroRef, audioStateRef }: Scene3DContentProps) {
  return (
    <>
      <ambientLight intensity={0.2} />
      <MouseTracker />
      <Particles />
      <ShootingStars heroRef={heroRef} audioStateRef={audioStateRef} />
    </>
  )
})

interface Scene3DProps {
  heroRef: React.RefObject<HTMLElement | null>
  audioStateRef: React.MutableRefObject<AudioState>
}

const Scene3D = memo(function Scene3D({ heroRef, audioStateRef }: Scene3DProps) {
  return (
    <div className="absolute inset-0 pointer-events-none">
      <Canvas
        camera={{ position: [0, 0, 7], fov: 50 }}
        dpr={[1, 1.5]}
        gl={{
          antialias: true,
          alpha: true,
          powerPreference: 'high-performance',
        }}
        style={{ background: 'transparent' }}
      >
        <Scene3DContent heroRef={heroRef} audioStateRef={audioStateRef} />
      </Canvas>
    </div>
  )
})

export default Scene3D
